// ── ProbeStrategy Pattern ──────────────────────────────────────────────
// Dispatches probe requests to either Cloud (API Gateway) or Local (browser SDK)
// based on the active execution mode.

import type {
  CallResult,
  NetworkProbeResult,
  ExecutionMode,
  ProfileType,
  Profile,
  PresetProfile,
} from '@/types'
import type { ProbeRequest, NetworkProbeRequest } from './api-client'
import { buildProbeRequestBody } from './api-client'
import { credentialsManager } from './credentials-manager'
import { sanitizeErrorMessage } from './security-utils'
import { getJwtToken } from './cognito-auth'
import { API_BASE } from '@/config/api'

// ── Helpers: read settings from localStorage ──────────────────────────

export function getExecutionMode(): ExecutionMode {
  try {
    const stored = localStorage.getItem('bedrock-diag-execution-mode')
    if (stored === 'local') return 'local'
  } catch { /* noop */ }
  return 'cloud'
}

function getApiGatewayUrl(): string {
  try {
    return localStorage.getItem('bedrock-diag-api-gateway-url') || ''
  } catch { /* noop */ }
  return ''
}

export function getSelectedRegion(): string {
  try {
    return localStorage.getItem('bedrock-diag-region') || 'us-east-1'
  } catch { /* noop */ }
  return 'us-east-1'
}

// ── Latency Simulation (browser-side, mirrors server.mjs behavior) ────

interface LatencySim {
  enabled: boolean
  connectDelayMs: number
  ttftDelayMs: number
  chunkDelayMs: number
}

let latencySim: LatencySim = { enabled: false, connectDelayMs: 0, ttftDelayMs: 0, chunkDelayMs: 0 }

/** Fetch current sim config from backend (Cloud) or localStorage (Local). Called once at strategy init. */
async function refreshLatencySim(): Promise<void> {
  try {
    if (getExecutionMode() === 'local') {
      const stored = localStorage.getItem('bedrock-diag-latency-sim')
      if (stored) latencySim = JSON.parse(stored)
      return
    }
    const token = await getJwtToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${API_BASE}/latency-sim`, { headers })
    if (res.ok) latencySim = await res.json()
  } catch { /* noop - sim stays disabled */ }
}

function sleep(ms: number): Promise<void> {
  return ms > 0 ? new Promise(r => setTimeout(r, ms)) : Promise.resolve()
}

// ── ProbeStrategy Interface ───────────────────────────────────────────

export interface ProbeStrategy {
  runProbe(req: ProbeRequest): Promise<CallResult>
  runNetworkProbe(req: NetworkProbeRequest): Promise<NetworkProbeResult>
}

// ── CloudProbeStrategy ────────────────────────────────────────────────

export class CloudProbeStrategy implements ProbeStrategy {
  private getBaseUrl(): string {
    const gatewayUrl = getApiGatewayUrl()
    if (gatewayUrl) return gatewayUrl.replace(/\/+$/, '')
    return API_BASE
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Include Cognito JWT token for authenticated cloud requests (Requirement 4.6)
    const jwtToken = await getJwtToken()
    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`
    }

    // Pass user-provided API keys to the backend (Requirement 6.9)
    const anthropicKey = await credentialsManager.getAnthropicApiKey()
    if (anthropicKey) {
      headers['X-Anthropic-Api-Key'] = anthropicKey
    }

    const xaiKey = await credentialsManager.getXaiApiKey()
    if (xaiKey) {
      headers['X-Xai-Api-Key'] = xaiKey
    }

    return headers
  }

  async runProbe(req: ProbeRequest): Promise<CallResult> {
    const baseUrl = this.getBaseUrl()
    const t0 = performance.now()
    const body = buildProbeRequestBody(req)
    const headers = await this.buildHeaders()

    const res = await fetch(`${baseUrl}/probe`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!res.ok) {
      const text = await res.text()
      throw new Error(`API error ${res.status}: ${text}`)
    }

    const resp = await res.json()
    const e2eTotalSec = (performance.now() - t0) / 1000
    const result: CallResult = resp.result
    result.e2eTotalSec = Math.round(e2eTotalSec * 1000) / 1000
    result.e2eOverheadMs = Math.round((e2eTotalSec - result.totalTimeSec) * 1000)
    result.executionMode = 'cloud'

    console.log(
      `[probe:cloud] ${result.profileName} success=${result.success} total=${result.totalTimeSec}s ttft=${result.ttftSec}s error=${result.error || 'none'}`
    )
    return result
  }

  async runNetworkProbe(req: NetworkProbeRequest): Promise<NetworkProbeResult> {
    const baseUrl = this.getBaseUrl()
    const headers = await this.buildHeaders()
    const res = await fetch(`${baseUrl}/network-probe`, {
      method: 'POST',
      headers,
      body: JSON.stringify(req),
    })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(`API error ${res.status}: ${text}`)
    }
    return res.json()
  }
}

// ── LocalProbeStrategy ────────────────────────────────────────────────

// Helper to determine profile type from ProbeRequest
function getProfileType(profile: Profile | PresetProfile): ProfileType {
  if ('isPreset' in profile && profile.isPreset) {
    if (profile.profileType?.toLowerCase().includes('direct')) return 'anthropic-direct'
    if (profile.profileType?.toLowerCase().includes('grok')) return 'grok'
    if (profile.guardrailId) return 'bedrock-guardrail'
    return 'bedrock'
  }
  return (profile as Profile).type
}

function getModelId(profile: Profile | PresetProfile): string {
  if ('isPreset' in profile && profile.isPreset) {
    return profile.modelId || (profile as PresetProfile).presetName
  }
  return (profile as Profile).modelId
}

function makeErrorResult(
  req: ProbeRequest,
  error: string,
  mode: ExecutionMode
): CallResult {
  const profileType = getProfileType(req.profile)
  return {
    callId: req.callId,
    profileId: req.profile.id,
    profileName: req.profile.name,
    profileType,
    prompt: req.profile.prompt.slice(0, 80),
    maxTokens: req.profile.maxTokens,
    success: false,
    totalTimeSec: 0,
    ttftSec: 0,
    maxGapSec: 0,
    chunkCount: 0,
    outputChars: 0,
    inputTokens: 0,
    outputTokens: 0,
    requestId: '',
    stopReason: '',
    bedrockInvocationLatencyMs: 0,
    bedrockFirstByteLatencyMs: 0,
    guardrailInputLatencyMs: 0,
    guardrailOutputLatencyMs: 0,
    guardrailTotalLatencyMs: 0,
    tokenThroughput: 0,
    chunkGaps: [],
    clientLatency: { sdkInitMs: 0, requestBuildMs: 0, clientProcessingMs: 0, roundTripOverheadMs: 0 },
    error,
    timestamp: new Date().toISOString(),
    executionMode: mode,
  }
}

export class LocalProbeStrategy implements ProbeStrategy {

  // ── Bedrock Probe (InvokeModelWithResponseStream) ───────────────────

  private async runBedrockProbe(req: ProbeRequest): Promise<CallResult> {
    const creds = await credentialsManager.getAwsCredentials()
    if (!creds) {
      console.error(`[probe:local:bedrock] No AWS credentials found for ${req.profile.name}. Auth method: ${credentialsManager.getAuthMethod()}, status: ${credentialsManager.getStatus()}`)
      return makeErrorResult(req, 'No AWS credentials available. Please provide credentials in Settings → AWS Credentials.', 'local')
    }

    const modelId = getModelId(req.profile)
    const profileType = getProfileType(req.profile)
    const isPreset = 'isPreset' in req.profile && req.profile.isPreset
    const profile = req.profile as Profile
    const presetProfile = req.profile as import('@/types').PresetProfile

    // Use preset's region if available, otherwise custom profile's region, then UI-selected region
    const region = isPreset
      ? (presetProfile.region || getSelectedRegion())
      : (profile.region || getSelectedRegion())

    // Resolve guardrail config from either preset or custom profile
    const guardrailId = isPreset ? presetProfile.guardrailId : profile.guardrailId
    const guardrailVersion = isPreset ? presetProfile.guardrailVersion : profile.guardrailVersion

    console.log(`[probe:local:bedrock:init] name=${req.profile.name} modelId=${modelId} region=${region} isPreset=${isPreset} guardrailId=${guardrailId || 'none'}`)

    // Dynamic import to keep bundle size down when not in local mode
    const { BedrockRuntimeClient, InvokeModelWithResponseStreamCommand } = await import(
      '@aws-sdk/client-bedrock-runtime'
    )

    const t0 = performance.now()

    let client: InstanceType<typeof BedrockRuntimeClient> | null = new BedrockRuntimeClient({
      region,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
      },
    })

    const sdkInitMs = performance.now() - t0

    // Build request body for Anthropic models on Bedrock
    const enableCaching = !isPreset && 'enablePromptCaching' in profile && profile.enablePromptCaching

    // Build user message content - use content blocks format when caching is enabled
    const userContent = enableCaching
      ? [{ type: 'text', text: req.profile.prompt, cache_control: { type: 'ephemeral' } }]
      : req.profile.prompt

    const requestBody: Record<string, unknown> = {
      anthropic_version: 'bedrock-2023-05-31',
      max_tokens: req.profile.maxTokens,
      messages: [{ role: 'user', content: userContent }],
    }

    // Add system prompt if present - use content blocks format when caching is enabled
    if ('systemPrompt' in profile && profile.systemPrompt) {
      if (enableCaching) {
        requestBody.system = [{ type: 'text', text: profile.systemPrompt, cache_control: { type: 'ephemeral' } }]
      } else {
        requestBody.system = profile.systemPrompt
      }
    }

    const requestBuildMs = performance.now() - t0 - sdkInitMs

    // Build command input
    const commandInput = {
      modelId,
      contentType: 'application/json',
      accept: 'application/json',
      body: new TextEncoder().encode(JSON.stringify(requestBody)),
      ...(guardrailId
        ? {
            guardrailIdentifier: guardrailId,
            guardrailVersion: guardrailVersion || 'DRAFT',
            trace: 'ENABLED' as const,
          }
        : {}),
    }

    try {
      // Refresh sim config and apply connect delay
      await refreshLatencySim()
      if (latencySim.enabled && latencySim.connectDelayMs > 0) await sleep(latencySim.connectDelayMs)

      const command = new InvokeModelWithResponseStreamCommand(commandInput)
      const response = await client.send(command)

      // Zero credential reference
      client = null

      // Collect streaming metrics
      let ttftSec = 0
      let chunkCount = 0
      let outputChars = 0
      let maxGapSec = 0
      const chunkGaps: number[] = []
      let lastChunkTime = performance.now()
      let firstChunkReceived = false
      const streamStartTime = performance.now()

      // Server-side metrics from Bedrock
      let bedrockInvocationLatencyMs = 0
      let bedrockFirstByteLatencyMs = 0
      let inputTokens = 0
      let outputTokens = 0
      let stopReason = ''
      let requestId = (response as any).$metadata?.requestId || ''
      let guardrailInputLatencyMs = 0
      let guardrailOutputLatencyMs = 0
      let guardrailTotalLatencyMs = 0
      let cacheCreationInputTokens = 0
      let cacheReadInputTokens = 0

      // Debug: log response shape
      console.log(`[probe:local:bedrock:response] keys=${Object.keys(response).join(',')} hasBody=${!!response.body} bodyType=${typeof response.body}`)

      if (response.body) {
        let eventIndex = 0
        for await (const event of response.body) {
          if (latencySim.enabled && latencySim.chunkDelayMs > 0) await sleep(latencySim.chunkDelayMs)
          eventIndex++
          const eventKeys = Object.keys(event)
          console.log(`[probe:local:bedrock:event#${eventIndex}] keys=${eventKeys.join(',')}`)

          // Check for invocation metrics as a top-level event property
          const eventAny = event as unknown as Record<string, unknown>
          if (eventAny['amazon-bedrock-invocationMetrics']) {
            const m = eventAny['amazon-bedrock-invocationMetrics'] as Record<string, number>
            bedrockInvocationLatencyMs = m.invocationLatency || 0
            bedrockFirstByteLatencyMs = m.firstByteLatency || 0
            inputTokens = (m.inputTokenCount as number) || inputTokens
            outputTokens = (m.outputTokenCount as number) || outputTokens
          }

          if (event.chunk?.bytes) {
            const now = performance.now()
            chunkCount++

            if (!firstChunkReceived) {
              if (latencySim.enabled && latencySim.ttftDelayMs > 0) await sleep(latencySim.ttftDelayMs)
              ttftSec = (performance.now() - t0) / 1000
              firstChunkReceived = true
            } else {
              const gapSec = (now - lastChunkTime) / 1000
              chunkGaps.push(gapSec)
              if (gapSec > maxGapSec) maxGapSec = gapSec
            }

            lastChunkTime = now

            const decoded = new TextDecoder().decode(event.chunk.bytes)
            try {
              const parsed = JSON.parse(decoded)
              if (parsed.delta?.text) {
                outputChars += parsed.delta.text.length
              }
              if (parsed.type === 'message_delta') {
                stopReason = parsed.delta?.stop_reason || stopReason
                outputTokens = parsed.usage?.output_tokens || outputTokens
              }
              if (parsed.type === 'message_start' && parsed.message?.usage) {
                inputTokens = parsed.message.usage.input_tokens || 0
                cacheCreationInputTokens = parsed.message.usage.cache_creation_input_tokens || 0
                cacheReadInputTokens = parsed.message.usage.cache_read_input_tokens || 0
              }
              if (parsed['amazon-bedrock-invocationMetrics']) {
                const m = parsed['amazon-bedrock-invocationMetrics']
                bedrockInvocationLatencyMs = m.invocationLatency || 0
                bedrockFirstByteLatencyMs = m.firstByteLatency || 0
                inputTokens = m.inputTokenCount || inputTokens
                outputTokens = m.outputTokenCount || outputTokens
              }
              if (parsed['amazon-bedrock-trace']?.guardrail) {
                const gr = parsed['amazon-bedrock-trace'].guardrail
                if (gr.input) {
                  for (const grId of Object.keys(gr.input)) {
                    const m = gr.input[grId]?.invocationMetrics
                    if (m?.guardrailProcessingLatency) guardrailInputLatencyMs = m.guardrailProcessingLatency
                  }
                }
                if (gr.outputs && Array.isArray(gr.outputs)) {
                  for (const oe of gr.outputs) {
                    for (const grId of Object.keys(oe)) {
                      const m = oe[grId]?.invocationMetrics
                      if (m?.guardrailProcessingLatency) guardrailOutputLatencyMs += m.guardrailProcessingLatency
                    }
                  }
                }
                guardrailTotalLatencyMs = guardrailInputLatencyMs + guardrailOutputLatencyMs
              }
            } catch {
              outputChars += decoded.length
            }
          }
        }
        console.log(`[probe:local:bedrock:stream] processed ${eventIndex} events, ${chunkCount} chunks`)
      } else {
        console.log('[probe:local:bedrock] response.body is falsy - no streaming events')
      }

      console.log(`[probe:local:bedrock:final] bedrockInvoc=${bedrockInvocationLatencyMs}ms bedrockFtb=${bedrockFirstByteLatencyMs}ms inputTokens=${inputTokens} outputTokens=${outputTokens} chunkCount=${chunkCount} ttft=${ttftSec}s cacheWrite=${cacheCreationInputTokens} cacheRead=${cacheReadInputTokens}`)

      const totalTimeSec = (performance.now() - t0) / 1000
      const clientProcessingMs = performance.now() - t0 - sdkInitMs - requestBuildMs

      // Fall back to estimated token counts if Bedrock didn't report them
      if (!inputTokens) inputTokens = Math.ceil(req.profile.prompt.length / 4)
      if (!outputTokens) outputTokens = Math.ceil(outputChars / 4)
      const tokenThroughput = totalTimeSec > 0 ? outputTokens / totalTimeSec : 0

      // In local mode, e2e and total are the same (no backend hop).
      // Overhead = browser total minus Bedrock-reported invocation time (SDK + network cost).
      const e2eTotalSec = totalTimeSec
      const e2eOverheadMs = bedrockInvocationLatencyMs > 0
        ? Math.round(totalTimeSec * 1000 - bedrockInvocationLatencyMs)
        : 0

      const result: CallResult = {
        callId: req.callId,
        profileId: req.profile.id,
        profileName: req.profile.name,
        profileType,
        prompt: req.profile.prompt.slice(0, 80),
        maxTokens: req.profile.maxTokens,
        success: true,
        totalTimeSec: Math.round(totalTimeSec * 1000) / 1000,
        // Always use browser-measured TTFT so the gap between TTFT and BR TTFT
        // shows the real network/SDK overhead to Bedrock.
        ttftSec: Math.round(ttftSec * 10000) / 10000,
        maxGapSec: Math.round(maxGapSec * 1000) / 1000,
        chunkCount,
        outputChars,
        inputTokens,
        outputTokens,
        requestId,
        stopReason: stopReason || 'end_turn',
        bedrockInvocationLatencyMs,
        bedrockFirstByteLatencyMs,
        guardrailInputLatencyMs,
        guardrailOutputLatencyMs,
        guardrailTotalLatencyMs,
        tokenThroughput: Math.round(tokenThroughput * 100) / 100,
        chunkGaps,
        clientLatency: {
          sdkInitMs: Math.round(sdkInitMs),
          requestBuildMs: Math.round(requestBuildMs),
          clientProcessingMs: Math.round(clientProcessingMs),
          roundTripOverheadMs: 0,
        },
        e2eTotalSec: Math.round(e2eTotalSec * 1000) / 1000,
        e2eOverheadMs,
        cacheCreationInputTokens,
        cacheReadInputTokens,
        promptCachingEnabled: enableCaching,
        timestamp: new Date().toISOString(),
        executionMode: 'local',
      }

      console.log(
        `[probe:local:bedrock] ${result.profileName} success=${result.success} total=${result.totalTimeSec}s ttft=${result.ttftSec}s brInvoc=${bedrockInvocationLatencyMs}ms brFtb=${bedrockFirstByteLatencyMs}ms`
      )
      return result
    } catch (err: unknown) {
      client = null
      const message = err instanceof Error ? err.message : String(err)
      const errorName = (err as { name?: string })?.name ?? ''
      console.error(`[probe:local:bedrock:error] ${req.profile.name}: ${errorName} - ${message}`)

      // Map AWS errors to credential-related messages
      let errorMsg: string
      if (errorName === 'ExpiredTokenException' || message.includes('expired')) {
        errorMsg = 'AWS credentials have expired. Please provide new credentials.'
      } else if (errorName === 'AccessDeniedException' || message.includes('Access Denied')) {
        errorMsg = 'Access denied. Check that your credentials have bedrock:InvokeModelWithResponseStream permission.'
      } else {
        errorMsg = sanitizeErrorMessage(message)
      }

      return makeErrorResult(req, errorMsg, 'local')
    }
  }

  // ── Anthropic Direct Probe (api.anthropic.com) ──────────────────────

  private async runAnthropicDirectProbe(req: ProbeRequest): Promise<CallResult> {
    let apiKey = await credentialsManager.getAnthropicApiKey()
    if (!apiKey) {
      return makeErrorResult(
        req,
        'No Anthropic API key available. Please provide your Anthropic API key in Settings.',
        'local'
      )
    }

    const modelId = getModelId(req.profile)
    const profileType = getProfileType(req.profile)
    const profile = req.profile as Profile

    const body: Record<string, unknown> = {
      model: modelId,
      max_tokens: req.profile.maxTokens,
      messages: [{ role: 'user', content: req.profile.prompt }],
      stream: true,
    }

    if ('systemPrompt' in profile && profile.systemPrompt) {
      body.system = profile.systemPrompt
    }

    const headers: Record<string, string> = {
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'content-type': 'application/json',
      'anthropic-dangerous-direct-browser-access': 'true',
    }

    // Zero the key variable after building headers
    apiKey = null as unknown as string

    const t0 = performance.now()

    try {
      await refreshLatencySim()
      if (latencySim.enabled && latencySim.connectDelayMs > 0) await sleep(latencySim.connectDelayMs)

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const text = await response.text()
        const errorMsg = text.includes('authentication') || text.includes('api_key')
          ? 'Anthropic API key is invalid or expired. Please check your API key.'
          : `Anthropic API error ${response.status}: ${sanitizeErrorMessage(text)}`
        return makeErrorResult(req, errorMsg, 'local')
      }

      // Parse SSE stream
      return await this.parseAnthropicStream(req, response, t0, profileType)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      return makeErrorResult(req, `Anthropic API call failed: ${sanitizeErrorMessage(message)}`, 'local')
    }
  }

  private async parseAnthropicStream(
    req: ProbeRequest,
    response: Response,
    t0: number,
    profileType: ProfileType
  ): Promise<CallResult> {
    const reader = response.body?.getReader()
    if (!reader) {
      return makeErrorResult(req, 'No response body from Anthropic API', 'local')
    }

    const decoder = new TextDecoder()
    let ttftSec = 0
    let chunkCount = 0
    let outputChars = 0
    let maxGapSec = 0
    const chunkGaps: number[] = []
    let lastChunkTime = performance.now()
    let firstChunkReceived = false
    let inputTokens = 0
    let outputTokens = 0
    let stopReason = ''
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)

            if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
              if (latencySim.enabled && latencySim.chunkDelayMs > 0) await sleep(latencySim.chunkDelayMs)
              const now = performance.now()
              chunkCount++
              outputChars += parsed.delta.text.length

              if (!firstChunkReceived) {
                if (latencySim.enabled && latencySim.ttftDelayMs > 0) await sleep(latencySim.ttftDelayMs)
                ttftSec = (performance.now() - t0) / 1000
                firstChunkReceived = true
              } else {
                const gapSec = (now - lastChunkTime) / 1000
                chunkGaps.push(gapSec)
                if (gapSec > maxGapSec) maxGapSec = gapSec
              }
              lastChunkTime = now
            }

            if (parsed.type === 'message_start' && parsed.message?.usage) {
              inputTokens = parsed.message.usage.input_tokens || 0
            }

            if (parsed.type === 'message_delta') {
              if (parsed.usage?.output_tokens) {
                outputTokens = parsed.usage.output_tokens
              }
              if (parsed.delta?.stop_reason) {
                stopReason = parsed.delta.stop_reason
              }
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    const totalTimeSec = (performance.now() - t0) / 1000
    if (!outputTokens) outputTokens = Math.ceil(outputChars / 4)
    const tokenThroughput = totalTimeSec > 0 ? outputTokens / totalTimeSec : 0

    const result: CallResult = {
      callId: req.callId,
      profileId: req.profile.id,
      profileName: req.profile.name,
      profileType,
      prompt: req.profile.prompt.slice(0, 80),
      maxTokens: req.profile.maxTokens,
      success: true,
      totalTimeSec: Math.round(totalTimeSec * 1000) / 1000,
      ttftSec: Math.round(ttftSec * 1000) / 1000,
      maxGapSec: Math.round(maxGapSec * 1000) / 1000,
      chunkCount,
      outputChars,
      inputTokens,
      outputTokens,
      requestId: '',
      stopReason,
      bedrockInvocationLatencyMs: 0,
      bedrockFirstByteLatencyMs: 0,
      guardrailInputLatencyMs: 0,
      guardrailOutputLatencyMs: 0,
      guardrailTotalLatencyMs: 0,
      tokenThroughput: Math.round(tokenThroughput * 100) / 100,
      chunkGaps,
      clientLatency: { sdkInitMs: 0, requestBuildMs: 0, clientProcessingMs: 0, roundTripOverheadMs: 0 },
      e2eTotalSec: Math.round(totalTimeSec * 1000) / 1000,
      e2eOverheadMs: 0,
      timestamp: new Date().toISOString(),
      executionMode: 'local',
    }

    console.log(
      `[probe:local:anthropic] ${result.profileName} success=${result.success} total=${result.totalTimeSec}s ttft=${result.ttftSec}s`
    )
    return result
  }

  // ── Grok Probe (api.x.ai) ──────────────────────────────────────────

  private async runGrokProbe(req: ProbeRequest): Promise<CallResult> {
    let apiKey = await credentialsManager.getXaiApiKey()
    if (!apiKey) {
      return makeErrorResult(
        req,
        'No xAI API key available. Please provide your xAI API key in Settings.',
        'local'
      )
    }

    const modelId = getModelId(req.profile)
    const profileType = getProfileType(req.profile)

    const body = {
      model: modelId,
      max_tokens: req.profile.maxTokens,
      messages: [{ role: 'user', content: req.profile.prompt }],
      stream: true,
    }

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    }

    // Zero the key variable after building headers
    apiKey = null as unknown as string

    const t0 = performance.now()

    try {
      await refreshLatencySim()
      if (latencySim.enabled && latencySim.connectDelayMs > 0) await sleep(latencySim.connectDelayMs)

      const response = await fetch('https://api.x.ai/v1/chat/completions', {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const text = await response.text()
        const errorMsg = text.includes('authentication') || text.includes('api_key') || text.includes('Unauthorized')
          ? 'xAI API key is invalid or expired. Please check your API key.'
          : `xAI API error ${response.status}: ${sanitizeErrorMessage(text)}`
        return makeErrorResult(req, errorMsg, 'local')
      }

      // Parse SSE stream (OpenAI-compatible format)
      return await this.parseGrokStream(req, response, t0, profileType)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      return makeErrorResult(req, `xAI API call failed: ${sanitizeErrorMessage(message)}`, 'local')
    }
  }

  private async parseGrokStream(
    req: ProbeRequest,
    response: Response,
    t0: number,
    profileType: ProfileType
  ): Promise<CallResult> {
    const reader = response.body?.getReader()
    if (!reader) {
      return makeErrorResult(req, 'No response body from xAI API', 'local')
    }

    const decoder = new TextDecoder()
    let ttftSec = 0
    let chunkCount = 0
    let outputChars = 0
    let maxGapSec = 0
    const chunkGaps: number[] = []
    let lastChunkTime = performance.now()
    let firstChunkReceived = false
    let outputTokens = 0
    let inputTokens = 0
    let stopReason = ''
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)

            // OpenAI-compatible streaming format
            const delta = parsed.choices?.[0]?.delta
            // Debug: log first few events to diagnose format differences
            if (chunkCount < 3 && !firstChunkReceived) {
              console.log(`[probe:local:grok:event] keys=${JSON.stringify(Object.keys(parsed))} choices=${JSON.stringify(parsed.choices?.[0])} delta=${JSON.stringify(delta)}`)
            }
            // Handle both regular content and reasoning content
            const content = delta?.content || delta?.reasoning_content
            if (content) {
              if (latencySim.enabled && latencySim.chunkDelayMs > 0) await sleep(latencySim.chunkDelayMs)
              const now = performance.now()
              chunkCount++
              outputChars += content.length

              if (!firstChunkReceived) {
                if (latencySim.enabled && latencySim.ttftDelayMs > 0) await sleep(latencySim.ttftDelayMs)
                ttftSec = (performance.now() - t0) / 1000
                firstChunkReceived = true
              } else {
                const gapSec = (now - lastChunkTime) / 1000
                chunkGaps.push(gapSec)
                if (gapSec > maxGapSec) maxGapSec = gapSec
              }
              lastChunkTime = now
            }

            // Capture finish reason
            if (parsed.choices?.[0]?.finish_reason) {
              stopReason = parsed.choices[0].finish_reason
            }

            // Capture usage if provided
            if (parsed.usage) {
              inputTokens = parsed.usage.prompt_tokens || inputTokens
              outputTokens = parsed.usage.completion_tokens || outputTokens
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    const totalTimeSec = (performance.now() - t0) / 1000
    if (!inputTokens) inputTokens = Math.ceil(req.profile.prompt.length / 4)
    if (!outputTokens) outputTokens = Math.ceil(outputChars / 4)
    const tokenThroughput = totalTimeSec > 0 ? outputTokens / totalTimeSec : 0

    const result: CallResult = {
      callId: req.callId,
      profileId: req.profile.id,
      profileName: req.profile.name,
      profileType,
      prompt: req.profile.prompt.slice(0, 80),
      maxTokens: req.profile.maxTokens,
      success: true,
      totalTimeSec: Math.round(totalTimeSec * 1000) / 1000,
      ttftSec: Math.round(ttftSec * 1000) / 1000,
      maxGapSec: Math.round(maxGapSec * 1000) / 1000,
      chunkCount,
      outputChars,
      inputTokens,
      outputTokens,
      requestId: '',
      stopReason,
      bedrockInvocationLatencyMs: 0,
      bedrockFirstByteLatencyMs: 0,
      guardrailInputLatencyMs: 0,
      guardrailOutputLatencyMs: 0,
      guardrailTotalLatencyMs: 0,
      tokenThroughput: Math.round(tokenThroughput * 100) / 100,
      chunkGaps,
      clientLatency: { sdkInitMs: 0, requestBuildMs: 0, clientProcessingMs: 0, roundTripOverheadMs: 0 },
      e2eTotalSec: Math.round(totalTimeSec * 1000) / 1000,
      e2eOverheadMs: 0,
      timestamp: new Date().toISOString(),
      executionMode: 'local',
    }

    console.log(
      `[probe:local:grok] ${result.profileName} success=${result.success} total=${result.totalTimeSec}s ttft=${result.ttftSec}s`
    )
    return result
  }

  // ── Main Dispatcher ─────────────────────────────────────────────────

  async runProbe(req: ProbeRequest): Promise<CallResult> {
    const profileType = getProfileType(req.profile)
    console.log(`[probe:local:dispatch] name=${req.profile.name} type=${profileType} isPreset=${'isPreset' in req.profile && req.profile.isPreset}`)

    switch (profileType) {
      case 'anthropic-direct':
        return this.runAnthropicDirectProbe(req)
      case 'grok':
        return this.runGrokProbe(req)
      case 'bedrock':
      case 'bedrock-guardrail':
      case 'bedrock-converse':
      case 'bedrock-proxy':
        return this.runBedrockProbe(req)
      default:
        return makeErrorResult(req, `Unsupported profile type for local mode: ${profileType}`, 'local')
    }
  }

  // ── Network Probe (Resource Timing API in local mode) ─────────────────
  // Uses fetch + Resource Timing API to extract DNS, TCP, TLS timing from the browser.
  // Falls back to total fetch duration if cross-origin restrictions zero out individual fields.

  async runNetworkProbe(req: NetworkProbeRequest): Promise<NetworkProbeResult> {
    const url = `https://${req.host}/`
    const conn = (navigator as any).connection
    const clientNetwork = conn ? {
      effectiveType: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
    } : undefined

    let dnsStatus: 'ok' | 'failed' | 'skipped' = 'skipped'
    let tcpStatus: 'ok' | 'failed' | 'timeout' | 'skipped' = 'skipped'
    let tlsStatus: 'ok' | 'failed' | 'skipped' = 'skipped'
    let errorType: string | undefined
    let coldMs = 0
    let warmMs = 0

    // Step 1: Cold request (DNS + TCP + TLS + HTTP)
    const t0 = performance.now()
    try {
      await fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store' })
      coldMs = performance.now() - t0
      // If we got here, all layers succeeded
      dnsStatus = 'ok'
      tcpStatus = 'ok'
      tlsStatus = 'ok'
    } catch (err: any) {
      coldMs = performance.now() - t0
      const msg = err?.message?.toLowerCase() || ''

      if (msg.includes('name') || msg.includes('dns') || msg.includes('resolve')) {
        dnsStatus = 'failed'
        tcpStatus = 'skipped'
        tlsStatus = 'skipped'
        errorType = 'ERR_NAME_NOT_RESOLVED'
      } else if (coldMs > 8000) {
        // Slow failure = likely firewall dropping packets (silent drop)
        dnsStatus = 'ok'
        tcpStatus = 'timeout'
        tlsStatus = 'skipped'
        errorType = 'ERR_CONNECTION_TIMED_OUT'
      } else if (msg.includes('refused') || msg.includes('reset')) {
        dnsStatus = 'ok'
        tcpStatus = 'failed'
        tlsStatus = 'skipped'
        errorType = 'ERR_CONNECTION_REFUSED'
      } else if (msg.includes('ssl') || msg.includes('tls') || msg.includes('cert')) {
        dnsStatus = 'ok'
        tcpStatus = 'ok'
        tlsStatus = 'failed'
        errorType = 'ERR_SSL_PROTOCOL_ERROR'
      } else {
        // Generic fetch error — likely CORS opaque response which is actually success
        // mode: 'no-cors' returns opaque response, fetch doesn't throw for it
        // If we're here with a short time, connection likely worked
        if (coldMs < 3000) {
          dnsStatus = 'ok'
          tcpStatus = 'ok'
          tlsStatus = 'ok'
        } else {
          dnsStatus = 'ok'
          tcpStatus = 'failed'
          tlsStatus = 'skipped'
          errorType = msg.slice(0, 80) || 'UNKNOWN_ERROR'
        }
      }
    }

    // Step 2: Warm request (reuses TCP connection — HTTP only)
    if (tcpStatus === 'ok' && tlsStatus === 'ok') {
      const t1 = performance.now()
      try {
        await fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store' })
      } catch { /* expected for cross-origin */ }
      warmMs = performance.now() - t1
    }

    const connectionSetupMs = tcpStatus === 'ok' ? Math.max(0, coldMs - warmMs) : coldMs
    const httpRoundTripMs = warmMs

    return {
      host: req.host,
      dnsLookupMs: dnsStatus === 'ok' ? Math.round(connectionSetupMs * 0.3) : 0, // estimate
      tcpConnectMs: tcpStatus === 'ok' ? Math.round(connectionSetupMs * 0.2) : 0, // estimate
      tlsHandshakeMs: tlsStatus === 'ok' ? Math.round(connectionSetupMs * 0.5) : 0, // estimate
      proxyConnectMs: 0,
      totalMs: Math.round(coldMs),
      error: errorType,
      timestamp: new Date().toISOString(),
      clientNetwork,
      diagnostic: {
        dnsStatus,
        tcpStatus,
        tlsStatus,
        connectionSetupMs: Math.round(connectionSetupMs),
        httpRoundTripMs: Math.round(httpRoundTripMs),
        errorType,
      },
    }
  }
}

// ── Strategy Dispatcher ───────────────────────────────────────────────

const cloudStrategy = new CloudProbeStrategy()
const localStrategy = new LocalProbeStrategy()

export function getActiveStrategy(): ProbeStrategy {
  const mode = getExecutionMode()
  return mode === 'local' ? localStrategy : cloudStrategy
}
